import { Component, DestroyRef, OnInit, QueryList, ViewChildren, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivityStreamState } from '../../data/activity-stream.state';
import {
  AddActivityStreamFilterParameter,
  ClearActivityStreamFilterParameterKey,
  RemoveActivityStreamFilterParameter
} from '../../data/activity-stream.actions';
import { ActivityStreamItem } from '@coin/shared/activity-stream/util';
import { ListViewFilterTagComponent } from '@coin/shared/core/feature-legacy-components';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { ListViewFilterTag, ListViewTagFilterParameter } from '@coin/shared/core/util-models';
import { Store } from '@ngxs/store';
import { forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ACTIVITY_STREAM_TAGS } from './activity-stream-tags.config';

@Component({
  selector: 'coin-c-activity-stream-tags',
  templateUrl: './activity-stream-tags.component.html',
  styleUrls: ['./activity-stream-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActivityStreamTagsComponent implements OnInit {
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);

  @ViewChildren(ListViewFilterTagComponent) filterItems: QueryList<ListViewFilterTagComponent>;

  filterParameter$ = this.store.select<ListViewTagFilterParameter[]>(ActivityStreamState.filterParameter);
  items$ = this.store.select<ActivityStreamItem[]>(ActivityStreamState.items);

  public tags: ListViewFilterTag[] = [];

  ngOnInit(): void {
    this.initializeTags();
    this.listenToActivityStreamItems();
  }

  private initializeTags(): void {
    this.tags = structuredClone(ACTIVITY_STREAM_TAGS);
  }

  private listenToActivityStreamItems(): void {
    this.items$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(items => this.setupFilterTags(items));
  }

  private setupFilterTags(items: ActivityStreamItem[]): void {
    for (const item of items) {
      const foundTag = this.tags.find(tag => tag.filterKey === 'itemType');
      const foundSubFilter = foundTag.subFilters.find(subFilter => subFilter.type === item.itemType);

      if (!foundSubFilter) {
        foundTag.subFilters = [
          ...foundTag.subFilters,
          { title: TinyHelpers.pascalcaseToText(item.itemType), type: item.itemType }
        ];
      }
    }
  }

  public addFilter(parameter: ListViewTagFilterParameter): void {
    this.store
      .dispatch(new AddActivityStreamFilterParameter(parameter))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public removeFilter(filter: ListViewTagFilterParameter): void {
    this.store.dispatch(new RemoveActivityStreamFilterParameter(filter));
  }

  public resetFilters(): void {
    const changes$ = this.filterItems.map(item =>
      this.store.dispatch(new ClearActivityStreamFilterParameterKey(item.category)).pipe(tap(() => item.reset()))
    );
    forkJoin(changes$).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
